// True when an API call failed with 401 or 403 (axios error shape: error.response.status). On the
// token endpoints this means the Docubird JWT doesn't belong to the requested account, or carries
// no account (spec §1.1, account-bound JWT; there is no grace window, D2).
export const isAuthRejection = (error: any): boolean => {
    const status: number | undefined = error?.response?.status;
    return status === 401 || status === 403;
};

// True when an API call got no answer at all: offline, a timeout, a dropped connection (axios
// rejects without error.response). The sign-in may still be good (review 2026-10-20).
export const isUnanswered = (error: any): boolean => error?.isAxiosError === true && !error.response;

// The status a failed request ended with: an axios error's response.status, a Graph SDK error's
// statusCode, or a plain number (a $batch sub-response's status). null when no answer came (axios
// without a response, the Graph SDK's statusCode -1); undefined when the failure isn't a request
// failure at all. The Graph SDK error is duck-typed: this file must not import it (2026-10-27).
// A Graph SDK error with a status below 400 is a body it could not read (a connection cut mid-transfer),
// reported with the status that came before it: no answer either (2026-10-28).
export const failureStatus = (failure: any): number | null | undefined => {
    if (typeof failure === "number") return failure;
    if (failure?.isAxiosError === true) return typeof failure.response?.status === "number" ? failure.response.status : null;
    if (typeof failure?.statusCode === "number") return failure.statusCode === -1 || failure.statusCode < 400 ? null : failure.statusCode;
    return undefined;
};

// True when a failed request says the service is down or busy, not what the user asked for: no
// answer, a 5xx, 408, 429, or a 401 while the token held has expired. Anything else is an answer
// (a 403, a 404, a bug that isn't a request failure), and what was kept must not stand in for it
// (2026-10-27).
export const isOutageFailure = (failure: any, tokenExpired: boolean): boolean => {
    const status = failureStatus(failure);
    if (status === undefined) return false;
    return status === null || status >= 500 || status === 408 || status === 429 || (status === 401 && tokenExpired);
};

// The exceptions MSAL throws when the user must sign in again: MsalUiRequiredException and its
// subclasses (a Conditional Access claims challenge, a refusal MSAL repeats from its throttle).
const interactionRequiredTypes: string[] = [
    "Microsoft.Identity.Client.MsalUiRequiredException",
    "Microsoft.Identity.Client.MsalClaimsChallengeException",
    "Microsoft.Identity.Client.MsalThrottledUiRequiredException"
];

// True when Microsoft wants the user to sign in again (MSAL's MsalUiRequiredException: a refresh
// token that expired or was revoked, a password change, a new MFA or Conditional Access rule). The
// API answers it with 400 interaction_required; before that it answered with a 500 whose body named
// the exception's own type (ExceptionMiddleware). Asking again only repeats the refusal (2026-10-26).
export const isInteractionRequired = (error: any): boolean =>
    error?.response?.data?.error === "interaction_required"
    || interactionRequiredTypes.indexOf(error?.response?.data?.ErrorType) !== -1;

// The time limit of each try of the six token calls (ApiService; before 2026-10-26 they had none).
// A refresh whose call ran this long got no answer (ranOutOfTime): a caller after it waits for it
// first (SharedRefreshes), as a caller waited for a call that never ended before 2026-10-26.
export const TOKEN_CALL_TIMEOUT_MS: number = 300000;

// True when a try ended because its own limit (config.timeout) ran out. axios rejects a request the
// browser aborted with ECONNABORTED too; only a timeout's message says it timed out (review
// 2026-10-28: an aborted try of a token call wasn't asked again, as it was before 2026-10-26).
export const timedOut = (error: any): boolean =>
    isUnanswered(error)
    && (error.code === "ETIMEDOUT" || (error.code === "ECONNABORTED" && /^timeout/.test(error.message ?? "")));

// True when a call ended because its own limit (config.timeout) ran out: no answer came in all that
// time, by the browser's own timer. Not a call aborted, or a connection that failed, sooner
// (2026-10-27). The wall clock isn't asked: a step back of a second during the 5 minutes made a
// run-out look like a call that failed sooner (review 2026-10-28).
export const ranOutOfTime = (error: any): boolean => timedOut(error) && error.config?.timeout > 0;

// True when a try failed because the device went offline: no connection (ERR_NETWORK) or one the
// browser aborted, while it reports being offline. That ended every connection, also a call that hung
// before 2026-10-26, when calls had no limit. Online, the same error can be a new connection refused
// while a hung one goes on (review 2026-10-28, the user's choice).
export const wentOffline = (error: any): boolean =>
    isUnanswered(error) && !timedOut(error) && (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED")
    && typeof navigator !== "undefined" && navigator.onLine === false;

// What a token call fails with when the API refused its JWT (401) and the JWT's renewal failed for
// a moment (no answer, a 5xx). Nothing refused the sign-in: like a call that got no answer, it keeps
// the sign-in, and nothing is logged twice (ApiService logged the renewal's failure). Review
// 2026-10-20. A renewal answered with a 5xx keeps it too, as before 2026-10-26: the sign-in
// recovers by itself after a short outage.
export const failedJwtRenewal = (): Error =>
    Object.assign(new Error("The Docubird JWT could not be renewed just now."), { isAxiosError: true });
